import axios, { AxiosError, AxiosRequestConfig } from 'axios';

const baseURL = import.meta.env.VITE_API_BASE ?? '/api';

export const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

let refreshPromise: Promise<unknown> | null = null;

/**
 * Flipped by auth.logout() while the /auth/logout request is in flight and
 * held there until cookies are visibly gone. The interceptor below checks
 * this before attempting a refresh, so a stray in-flight request that 401s
 * mid-logout can't bring the session back via the refresh cookie that's
 * about to be cleared. Without this guard we hit the user-reported race:
 * "sign out → hard refresh shows authed user → sign out again clears it".
 */
let loggingOut = false;
export function setLoggingOut(v: boolean): void {
  loggingOut = v;
}

api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (!original) return Promise.reject(err);

    if (
      err.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes('/auth/') &&
      !loggingOut
    ) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${baseURL}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => {
              refreshPromise = null;
            });
        }
        await refreshPromise;
        return api(original);
      } catch (refreshErr) {
        // Only bounce to login when the user is actually sitting on a gated
        // page. App.vue probes /users/me on every mount, so an anonymous
        // visitor 401s here on public pages too — redirecting on that would
        // make the whole storefront login-only for logged-out visitors.
        const { router } = await import('../router');
        const current = router.currentRoute.value;
        if (current.meta.requiresAuth && current.name !== 'login') {
          router.replace({ name: 'login', query: { next: current.fullPath } });
        }
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(err);
  },
);
